import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { logout, refreshToken, setTokens } from "redux/slices/authSlice";
import env from "env";

type BaseQuery = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;

const handleClear = async (dispatch: any) => {
  const isAutoNavigateWso2Auth =
    env.app.authResourceOwner === "false" && env.app.authCodeEnabled === "true";

  await dispatch(logout());

  setTimeout(() => {
    if (isAutoNavigateWso2Auth) {
      window.location.href = `${env.api.baseUrl.tokenBaseUrl}oidc/logout?client_id=${env.app.authCodeAppId}&post_logout_redirect_uri=${env.app.rootUrl}`;
    } else {
      window.location.href = "/";
    }
  }, 300);
};

export const baseQueryCreator = (baseQuery: BaseQuery) => {
  const baseQueryWithReauth: BaseQuery = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);
    const { getState, dispatch }: any = api;

    if (result.error && result.error.status === 401) {
      // try to get a new token
      try {
        const token = getState().auth.refreshToken;
        if (token) {
          const resp = await (dispatch)(refreshToken()).unwrap();

          if (resp && resp.access_token && resp.refresh_token) {
            // store the new token
            dispatch(setTokens(resp));
            // retry the initial query
            result = await baseQuery(args, api, extraOptions);
          } else {
            handleClear(dispatch);
          }
        } else {
          handleClear(dispatch);
        }
      } catch {
        handleClear(dispatch);
      }
    } else if (result.error && result.error.status === "FETCH_ERROR") {
      handleClear(dispatch);
    }

    return result;
  };

  return baseQueryWithReauth;
};

export const baseQueryWithAppToken = (baseQuery: BaseQuery) => {
  const baseQueryCustom: BaseQuery = async (args: any, api, extraOptions) => {
    const response = await fetch(
      `${env.api.baseUrl.tokenBaseUrl}oauth2/token`,
      {
        method: "POST",
        headers: {
          Authorization: `Basic ${btoa(
            `${env.api.clientId}:${env.api.clientSecret}`
          )}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: (function () {
          const formBody = [{ key: "grant_type", value: "client_credentials" }]
            .map(
              (n) =>
                encodeURIComponent(n.key) + "=" + encodeURIComponent(n.value)
            )
            .join("&");
          return formBody;
        })(),
      }
    );
    const content = await response.json();
    args.headers = {
      ...args.headers,
      Authorization: `Bearer ${content?.access_token}`,
    };
    let result: any = await baseQuery(args, api, extraOptions);
    if (result?.error?.data) {
      return {
        error: result?.error,
      };
    }
    return result;
  };

  return baseQueryCustom;
};

// https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#automatic-re-authorization-by-extending-fetchbasequery
